import { useQuery } from "@tanstack/react-query";
import { useState, useEffect, useMemo } from "react";
import { format } from "date-fns";
import { supabase } from "@/lib/supabase";
import { Invoice, Client, Project, InvoiceGroup } from "@/types/invoice";
import { useUser } from "@/contexts/UserContext";

export const useInvoices = () => {
  const { companyId } = useUser(); // Get companyId from UserContext
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [defaultOpenValue, setDefaultOpenValue] = useState<string | undefined>(undefined);

  const { data: invoices, isLoading, refetch } = useQuery({
    queryKey: ['invoices', companyId],
    queryFn: async () => {
      try {
        if (!companyId) throw new Error('No company selected');

        const { data: companyClients, error: clientsError } = await supabase
          .from('clients')
          .select(`
            id, 
            name,
            short_name,
            legal_name,
            billing_details
          `)
          .eq('company_id', companyId);
          
        if (clientsError) {
          console.error("Error fetching clients:", clientsError);
          throw clientsError;
        }
        
        const clientIds = companyClients.map(c => c.id);
        
        const { data: companyInvoices, error: invoicesError } = await supabase
          .from('invoices')
          .select(`
            id,
            client_id,
            invoice_number,
            amount,
            currency,
            status,
            billing_period_start,
            billing_period_end,
            milestone_id
          `)
          .in('client_id', clientIds)
          .order('billing_period_start', { ascending: false });

        if (invoicesError) {
          console.error("Error fetching invoices:", invoicesError);
          throw invoicesError;
        }
        
        console.log("Fetched invoices:", companyInvoices);
        
        setClients(companyClients as Client[]);
        
        return companyInvoices as Invoice[];
      } catch (error) {
        console.error("Error in invoice query:", error);
        throw error;
      }
    },
    enabled: !!companyId // Only run query when companyId is available
  });

  const groupedInvoices = useMemo(() => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
    const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
    
    const currentPeriodKey = format(now, 'yyyy-MM');
    const currentPeriodDisplay = format(now, 'MMMM yyyy');
    
    if (!invoices || invoices.length === 0) {
      const currentPeriod: InvoiceGroup = {
        periodKey: currentPeriodKey,
        period: currentPeriodDisplay,
        invoices: [],
        isCurrent: true,
        fromDate: firstDayOfMonth,
        toDate: lastDayOfMonth
      };
      
      setDefaultOpenValue(currentPeriodKey);
      
      return [currentPeriod];
    }

    const groups: Record<string, Invoice[]> = {};

    groups[currentPeriodKey] = [];

    invoices.forEach(invoice => {
      const periodStart = invoice.billing_period_start;
      if (!periodStart) return;

      const periodKey = periodStart.substring(0, 7);
      
      if (!groups[periodKey]) {
        groups[periodKey] = [];
      }
      
      groups[periodKey].push(invoice);
    });

    const groupedResults = Object.entries(groups)
      .map(([periodKey, periodInvoices]) => {
        const [year, monthStr] = periodKey.split('-');
        const month = parseInt(monthStr, 10) - 1;
        
        const fromDate = new Date(parseInt(year, 10), month, 1);
        const toDate = new Date(parseInt(year, 10), month + 1, 0);
        
        const date = new Date(parseInt(year, 10), month);
        
        const formattedPeriod = format(date, 'MMMM yyyy');
        
        const isCurrent = periodKey === currentPeriodKey;
        
        return {
          periodKey,
          period: formattedPeriod,
          invoices: periodInvoices,
          isCurrent,
          fromDate,
          toDate
        };
      })
      .sort((a, b) => b.periodKey.localeCompare(a.periodKey));
      
    const currentPeriodIndex = groupedResults.findIndex(group => group.isCurrent);
    if (currentPeriodIndex > 0) {
      const currentPeriod = groupedResults.splice(currentPeriodIndex, 1)[0];
      groupedResults.unshift(currentPeriod);
    }
      
    setDefaultOpenValue(currentPeriodKey);
    
    return groupedResults;
  }, [invoices]);

  useEffect(() => {
    const loadClientsAndProjects = async () => {
      try {
        if (!companyId) return;

        console.log("Loading clients and projects for company ID:", companyId);
        
        const { data: clientsData, error: clientsError } = await supabase
          .from('clients')
          .select(`
            id, 
            name,
            short_name,
            legal_name,
            billing_details
          `)
          .eq('company_id', companyId);

        if (clientsError) {
          console.error("Error fetching clients:", clientsError);
          return;
        }

        if (clientsData) {
          setClients(clientsData as Client[]);
        }

        const { data: projectsData, error: projectsError } = await supabase
          .from('projects')
          .select(`
            id,
            name,
            type,
            client_id
          `)
          .eq('client.company_id', companyId);

        if (projectsError) {
          console.error("Error fetching projects:", projectsError);
          return;
        }

        if (projectsData) {
          setProjects(projectsData);
        }
      } catch (error) {
        console.error("Exception loading data:", error);
      }
    };

    loadClientsAndProjects();
  }, [companyId]); // Only depends on companyId from context

  return {
    invoices,
    isLoading,
    refetch,
    clients,
    projects,
    groupedInvoices,
    defaultOpenValue
  };
};
